import React from 'react';
import { Await, defer, LoaderFunctionArgs, useAsyncValue, useLoaderData } from 'react-router-dom';
import apiFetch, { genericErrorMessage } from '@/services/apiFetch.ts';
import { getCatBContracts } from '@/services/category.endpoint.ts';
import { CatBCostContractObj } from 'interfaces/category.interface.ts';
import { Loading } from 'components/primitive';
import { ErrorHandler } from 'components/fragment';

const DetailsPage = React.lazy(() => import('./Detail.container.tsx'));

interface ContractDetailsResponse {
  data: CatBCostContractObj | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const loaderContractDetails = ({ params }: LoaderFunctionArgs) => {
  try {
    const { id } = params;
    if (id) {
      const endpoint = getCatBContracts(id);
      const response = apiFetch<CatBCostContractObj>(endpoint).then((res) => res.data ?? null);
      return defer({
        data: response,
      });
    } else {
      return defer({
        data: null,
      });
    }
  } catch (error) {
    let message = genericErrorMessage;
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    throw Error(message);
  }
};

function RenderAwaitedData(props: { newEntry: boolean }) {
  const { newEntry } = props;
  const data = useAsyncValue() as CatBCostContractObj | null;
  const isNew = newEntry ?? data === null;
  return <DetailsPage newEntry={isNew} data={data} />;
}

const DetailContainer: React.FC<{ newEntry: boolean }> = ({ newEntry }) => {
  const data = useLoaderData() as ContractDetailsResponse;
  return (
    <React.Suspense fallback={<Loading />}>
      <Await
        resolve={data.data}
        errorElement={
          <ErrorHandler headerTitle='Category B: Contracts' errorMsg='Something went wrong' />
        }>
        <RenderAwaitedData newEntry={newEntry} />
      </Await>
    </React.Suspense>
  );
};

export default DetailContainer;
